/**
 * Öffentliche Startseite.
 *
 * Erklärt, was die Software tut, und bietet drei Wege hinein: anmelden,
 * registrieren oder die Demo ansehen. Sie ist die einzige Seite, die jemand
 * sieht, bevor er ein Konto hat — deshalb steht hier nichts, was man erst nach
 * dem Anmelden verstehen kann.
 *
 * Angemeldete kommen hier nie an; `App.tsx` zeigt ihnen unter `/` die
 * Übersicht.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'

import { api, ApiError, setCsrfToken, type Me } from '../lib/api'
import { listItemVariants, listVariants, springSoft } from '../lib/motion'
import { ME_KEY } from '../lib/session'
import { Banner, Button } from '../ui'
import { Footer } from '../components/Shell'
import './landing.css'

/**
 * Was die Software tut — in der Sprache eines Wirts, nicht in unserer.
 *
 * Bewusst keine Aufzählung von Datenmodellen. „Mandantenfähig mit Row Level
 * Security" beeindruckt Entwickler und sagt einem Gastronomen nichts.
 */
const FUNKTIONEN = [
  {
    titel: 'Planen, wie man denkt',
    text:
      'Wochenraster, Tages-Timeline nach Standort und Position, Monatsübersicht. ' +
      'Schichten per Ziehen verschieben — auch auf eine andere Position.',
  },
  {
    titel: 'Das Arbeitszeitgesetz im Blick',
    text:
      'Ruhezeit, Höchstarbeitszeit und Pausen werden beim Einteilen geprüft. ' +
      'Die Warnung blockiert nicht — sie verlangt eine Begründung, und die steht danach im Protokoll.',
  },
  {
    titel: 'Ohne Konto verplanbar',
    text:
      'Die Aushilfe arbeitet ab Montag, ob sie sich anmeldet oder nicht. ' +
      'Personal anlegen geht ohne E-Mail-Adresse; ein Zugang lässt sich jederzeit nachreichen.',
  },
  {
    titel: 'Selfservice statt Rückrufe',
    text:
      'Verfügbarkeiten, Urlaubsanträge, Tauschbörse und Bewerbungen auf offene Schichten. ' +
      'Der Plan landet als Kalender-Abo auf dem Telefon.',
  },
  {
    titel: 'Urlaub, der aufgeht',
    text:
      'Anspruch je Jahr, Übertrag, Resttage. Gezählt in Werktagen nach Bundesurlaubsgesetz — ' +
      'Feiertage kosten keinen Urlaubstag.',
  },
  {
    titel: 'Personalkosten pro Tag',
    text:
      'Geplante Kosten gegen den Umsatz, als Quote. Sichtbar ausschließlich für den Inhaber — ' +
      'aus einer Tagessumme ließe sich sonst der Einzellohn zurückrechnen.',
  },
]

export function LandingPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const demo = useMutation({
    mutationFn: () => api.post<Me>('/api/auth/demo'),
    onSuccess: (nutzer) => {
      // Derselbe Weg wie nach einer normalen Anmeldung: CSRF-Wert setzen, das
      // Nutzerobjekt in den Zwischenspeicher legen, weiterspringen. Ohne
      // beides fragte die App direkt nach dem Sprung erneut nach und zeigte
      // für einen Moment die Anmeldeseite.
      setCsrfToken(nutzer.csrf_token)
      queryClient.setQueryData(ME_KEY, nutzer)
      // Direkt in den Plan statt auf die Übersicht: Dort steht der Kalender,
      // und deswegen klickt jemand auf „Demo ansehen".
      navigate('/plan', { replace: true })
    },
  })

  return (
    <div className="landing">
      <header className="landing__bar">
        <span className="landing__brand">
          <LogoMark />
          Dienstplan
        </span>
        <nav className="landing__nav">
          <Link to="/anmelden" className="landing__link">
            Anmelden
          </Link>
          <Button small onClick={() => navigate('/registrieren')}>
            Kostenlos starten
          </Button>
        </nav>
      </header>

      <main id="inhalt">
        <motion.section
          className="landing__hero"
          variants={listVariants(3)}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 variants={listItemVariants} transition={springSoft()}>
            Der Dienstplan, der das Arbeitszeitgesetz kennt.
          </motion.h1>
          <motion.p
            className="landing__lead"
            variants={listItemVariants}
            transition={springSoft()}
          >
            Für Sicherheitsdienste, Gastronomie, Pflege, Handel — jeden Betrieb, in dem
            Menschen in wechselnden Diensten arbeiten. Planen, tauschen, Urlaub verwalten und
            die Personalkosten im Blick behalten. Gebaut für einen Betrieb im Alltag, nicht für
            eine Produktvorführung.
          </motion.p>
          <motion.div
            className="landing__cta"
            variants={listItemVariants}
            transition={springSoft()}
          >
            <Button onClick={() => demo.mutate()} busy={demo.isPending}>
              Live-Demo ansehen
            </Button>
            <Button variant="outlined" onClick={() => navigate('/registrieren')}>
              Eigenen Betrieb anlegen
            </Button>
          </motion.div>

          {demo.isError && (
            <Banner tone="error">
              {(demo.error as ApiError).status === 404
                ? 'Die Demo steht gerade nicht bereit. Melde dich an oder lege einen eigenen Betrieb an.'
                : (demo.error as ApiError).message}
            </Banner>
          )}

          <p className="landing__hint">
            Die Demo öffnet einen fertig eingerichteten Beispielbetrieb — mit Belegschaft, Plan
            und Auswertungen. Alles darin lässt sich anfassen; jede Nacht wird zurückgesetzt.
          </p>
        </motion.section>

        <section className="landing__section" aria-labelledby="funktionen">
          <h2 id="funktionen">Was drin ist</h2>
          <motion.ul
            className="landing__grid"
            variants={listVariants(FUNKTIONEN.length)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            {FUNKTIONEN.map((funktion) => (
              <motion.li
                key={funktion.titel}
                className="landing__feature"
                variants={listItemVariants}
                transition={springSoft()}
              >
                <h3>{funktion.titel}</h3>
                <p>{funktion.text}</p>
              </motion.li>
            ))}
          </motion.ul>
        </section>

        <section className="landing__section landing__section--tight" aria-labelledby="ernst">
          <h2 id="ernst">Wo wir genauer hinsehen</h2>
          <div className="landing__grid landing__grid--two">
            <div className="landing__note">
              <h3>Getrennte Betriebe</h3>
              <p>
                Jeder Betrieb sieht ausschließlich seine eigenen Daten — abgesichert nicht nur in
                der Anwendung, sondern in der Datenbank selbst. Ein vergessener Filter im Code
                führt hier zu keinem Datenleck.
              </p>
            </div>
            <div className="landing__note">
              <h3>Sparsam mit Personendaten</h3>
              <p>
                Abwesenheiten haben eine Art, aber keinen Grund. Push-Nachrichten sagen,{' '}
                <em>dass</em> etwas vorliegt, nie was — sie erscheinen auf dem Sperrbildschirm.
                Der Kalender-Abo nennt keine Namen anderer Personen.
              </p>
            </div>
            <div className="landing__note">
              <h3>Zeiten, die stimmen</h3>
              <p>
                Gespeichert wird in UTC, gerechnet in der Zeitzone des Betriebs. Nachtschichten
                laufen über Mitternacht, und der Tag der Zeitumstellung hat keine 24 Stunden —
                beides ist berücksichtigt.
              </p>
            </div>
            <div className="landing__note">
              <h3>Nachvollziehbar</h3>
              <p>
                Anmeldungen, Rollenwechsel, Veröffentlichungen und jede Einteilung entgegen einer
                Warnung stehen im Protokoll. Es lässt sich nicht nachträglich ändern.
              </p>
            </div>
          </div>
        </section>

        <section className="landing__section landing__abschluss">
          <h2>Selbst ausprobieren</h2>
          <p className="landing__lead">
            Kein Formular, keine Kreditkarte — die Demo öffnet sich mit einem Klick.
          </p>
          <div className="landing__cta">
            <Button onClick={() => demo.mutate()} busy={demo.isPending}>
              Live-Demo ansehen
            </Button>
            <Link to="/anmelden" className="landing__link">
              Ich habe schon ein Konto
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

/** Dasselbe Zeichen wie in der Kopfleiste der App — Wiedererkennung. */
function LogoMark() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.6em"
      height="1.6em"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4.5" width="18" height="16" rx="4" />
      <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
      <path d="M7.5 14h3M13.5 14h3M7.5 17.2h3" />
    </svg>
  )
}
